import BaseController from './BaseController';
import SearchDataSource from '../dataSources/SearchDataSource';
import OmniSocialExplorerSearchDataSource from '../dataSources/OmniSocialExplorerSearchDataSource';
import ProjectDataSource from '../dataSources/ProjectDataSource';
import Errors from '../enums/Error';
import Limits from '../enums/SearchLimit';
import { parseSocexResult, parseHereGeocodeResult } from '../helpers/Search';

class SearchController extends BaseController {
    static get name() {
        return 'SearchController';
    }

    static getInstance(options) {
        return new SearchController(options);
    }

    onActivate() {
        this.bindGluBusEvents({
            SEARCH_LOAD_REQUEST: this.onSearchLoadRequest,
            SEARCH_GEOCODE_AND_ENRICH_SINGLE_ITEM: this.onSearchGeocodeSingleItem,
            SEARCH_GET_RECENT_SEARCH_TERMS_REQUEST: this.onGetRecentSearchTermsRequest,
            SEARCH_ADD_RECENT_SEARCH_TERM_REQUEST: this.onAddRecentSearchTermRequest,
            REVERSE_GEOCODE_REQUEST: this.onReverseGeocodeRequest,
            GEOCODE_SEARCH_REQUEST: this.onGeocodeSearchRequest,
        });

        this.omniSocialExplorerSearchDatasource = this.activateSource(
            OmniSocialExplorerSearchDataSource,
        );
        this.searchDataSource = this.activateSource(SearchDataSource);
        this.projectDataSource = this.activateSource(ProjectDataSource);
        /** @type {(result: any) => import('../../..').SearchResult} */
        this.parseFunction = parseSocexResult;
    }

    onGetRecentSearchTermsRequest = () => {
        const recentSearchTerms = this.searchDataSource.recentSearchTerms;

        this.bus.emit('SEARCH_GET_CURRENT_RECENT_SEARCH_TERMS_RESPONSE', {
            source: this,
            recentSearchTerms,
        });
    };

    onAddRecentSearchTermRequest = e => {
        const recentSearchTerms = this.searchDataSource.addRecentSearchTerm(e.searchTerm);

        this.bus.emit('SEARCH_ADD_RECENT_SEARCH_TERM_SUCCESS', { source: this, recentSearchTerms });
    };

    /**
     * Event handler for search.
     *
     *      Params is the object which contains information about
     *      viewBox bounds and search term.
     *      In controller, it is appended with limit parameter and passed
     *      to SearchDataSource.
     *      params has the following objects:
     *          searchTerm: the string by which suggestions are retrieved.
     *          mapInstanceId: id of the map where search is,
     *          map: information about the map (contains viewBox bounds)
     * @param item
     */

    // For the search result without spatial data, we need to geocode it using the value/label from the first step
    onSearchGeocodeSingleItem(item) {
        const params = {
            searchTerm: item.value, // Use previously returned place label as the 2nd step geocoding
            limit: 1, // Geocoding with search limit 1 - returning most likely result only
        };

        this.searchDataSource
            .hereGeocode(params)
            .then(results => {
                this.bus.emit('SEARCH_SINGLE_ITEM_LOAD_SUCCESS', {
                    ...parseHereGeocodeResult(results.data[0]),
                    id: item.id, // Keep the same id from previous/master search
                });
            })
            .catch(error => {
                this.bus.emit('SEARCH_LOAD_ERROR', {
                    level: Errors.Error,
                    originalError: error,
                    additionalInfo:
                        'Geo-coding explicit item failed. Check availability of Here geocoder',
                });
            });
    }

    onSearchLoadRequest(params) {
        if (!params.searchTerm || !params.mapInstanceId) return;

        params.limit = Limits.SEARCH_LIMIT;

        const onError = error => {
            this.bus.emit('SEARCH_LOAD_ERROR', {
                level: Errors.Error,
                originalError: error,
                additionalInfo: 'Searching for POIs failed. Check availability of geocoder',
            });
        };

        this.searchDataSource
            .hereGeocode(params)
            .then(results => {
                this.parseFunction = parseHereGeocodeResult;
                const data = this.parseResults(results.data);
                const response = {
                    mapInstanceId: params.mapInstanceId,
                    data,
                    query: results.query,
                };
                this.bus.emit('SEARCH_LOAD_SUCCESS', response);
            })
            .catch(onError);
    }

    /**
     * Forward-geocodes a free-text query for address autosuggest inputs.
     * @param {object} param0
     * @param {string} param0.query
     */
    onGeocodeSearchRequest({ query }) {
        // hereGeocode swallows its own errors and resolves with an empty list
        this.searchDataSource.hereGeocode({ searchTerm: query }).then(({ data }) => {
            this.bus.emit('GEOCODE_SEARCH_RESPONSE', { query, data });
        });
    }

    /**
     * @param {object} param0
     * @param {import('../types').Point} param0.point
     */
    onReverseGeocodeRequest({ point }) {
        // HERE api from Rails backend
        this.searchDataSource
            .hereReverseGeocode({ point })
            .then(address => {
                this.bus.emit('REVERSE_GEOCODE_RESPONSE', { point, address });
            })
            .catch(error => {
                // TODO: avoid using catch. Reason for this is that this will catch
                // on promise. This will catch error done while rendering a
                // view. Instead, pass function as a second parameter of the
                // then method
                this.bus.emit('REVERSE_GEOCODE_ERROR', {
                    level: Errors.Error,
                    originalError: error,
                    additionalInfo: 'Reverse geocode failed.',
                });
            });
    }

    /**
     * Function that handles multiple results.
     * Parses each one and returns parsed array.
     * @param {[]} results
     *         Array of results to be parsed
     * @returns {import('../../..').SearchResult[]}
     *         Returns array of parsed results
     */
    parseResults(results) {
        return results.map(result => this.parseFunction(result));
    }

    onDeactivate() {
        this.unbindGluBusEvents();
    }
}

export default SearchController;
